/**
 * Pick-up ranges of the BI dashboards and the capture window of each comparison (CU-869fmhmx8 D4, CA 6-7).
 * A relative range is a key, resolved against "today" every time, so a link opened another day moves with it.
 */
import { addDays, daysBetween, maxDate, minDate, startOfDay } from './dates.js'

export const RANGE_GROUPS = ['Upcoming', 'Season', 'Looking back', 'Fixed']

export const RANGES = Object.freeze({
	next4w: { label: 'Next 4 weeks', group: 'Upcoming' },
	next8w: { label: 'Next 8 weeks', group: 'Upcoming' },
	next12w: { label: 'Next 12 weeks', group: 'Upcoming' },
	curNext: { label: 'Current and next month', group: 'Season' },
	last4w: { label: 'Last 4 weeks', group: 'Looking back' },
	last8w: { label: 'Last 8 weeks', group: 'Looking back' },
	fixed: { label: 'Fixed dates', group: 'Fixed' },
})

export const DEFAULT_RANGE = 'curNext'

// D4: the server refuses longer views (400 range_too_long)
export const MAX_PICKUP_DAYS = 180
export const MAX_CAPTURE_DAYS = 200
// how far before the first pick-up the last-year window starts (the profile of P1: captures 1-3 months ahead)
export const CAPTURE_LEAD_DAYS = 84

export const isRangeKey = key => Object.prototype.hasOwnProperty.call(RANGES, key)

/** `{ from, to }` (local dates) of a range; `fixed` needs `{ from, to }`. `null` when it cannot be resolved. */
export function resolveRange(key, today, fixed = null) {
	const t = startOfDay(today)
	switch (key) {
		case 'next4w': return { from: t, to: addDays(t, 27) }
		case 'next8w': return { from: t, to: addDays(t, 55) }
		case 'next12w': return { from: t, to: addDays(t, 83) }
		case 'curNext': return { from: new Date(t.getFullYear(), t.getMonth(), 1), to: new Date(t.getFullYear(), t.getMonth() + 2, 0) }
		case 'last4w': return { from: addDays(t, -28), to: addDays(t, -1) }
		case 'last8w': return { from: addDays(t, -56), to: addDays(t, -1) }
		case 'fixed': return isValidFixed(fixed) ? { from: startOfDay(fixed.from), to: startOfDay(fixed.to) } : null
		default: return null
	}
}

/** A fixed range the server accepts: both ends, in order, at most 180 days */
export function isValidFixed(fixed) {
	if (!(fixed?.from instanceof Date) || !(fixed?.to instanceof Date)) return false
	if (Number.isNaN(fixed.from.getTime()) || Number.isNaN(fixed.to.getTime())) return false
	const days = daysBetween(fixed.from, fixed.to) + 1
	return days >= 1 && days <= MAX_PICKUP_DAYS
}

/**
 * "Prices captured: Up to yesterday" (D4), as the dates sent as `startBookingDate/endBookingDate`:
 *  - last year: from 84 days before the first pick-up to yesterday (or the last pick-up, if earlier). For pick-ups
 *    further ahead, the last 84 days; never longer than the 200 days the server accepts;
 *  - last week: the 7 days up to yesterday (compared with the 7 before);
 *  - yesterday: yesterday (compared with the day before).
 */
export function captureWindow(compare, pickupFrom, pickupTo, today) {
	const yesterday = addDays(startOfDay(today), -1)
	if (compare === 'lw') return { from: addDays(yesterday, -6), to: yesterday }
	if (compare === 'yday') return { from: yesterday, to: yesterday }

	const to = minDate(yesterday, startOfDay(pickupTo))
	let from = minDate(addDays(startOfDay(pickupFrom), -CAPTURE_LEAD_DAYS), addDays(to, -(CAPTURE_LEAD_DAYS - 1)))
	from = maxDate(from, addDays(to, -(MAX_CAPTURE_DAYS - 1)))
	return { from, to }
}
